import "../style.module.scss";

import { East, Visibility } from "@mui/icons-material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import KeyboardDoubleArrowRightRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import { Drawer, TableCell, TableRow, Tooltip } from "@mui/material";
import classNames from "classnames";
import CurrencyIcon from "components/shared/CurrencyIcon";
import DeleteModal from "components/shared/DeleteModal";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import ManagementTableAuthorComponent from "components/shared/ManagementTableAuthorComponent";
import ModalBase from "components/shared/ModalBase";
import TextButton from "components/shared/TextButton";
import type { ThreeDotMenuOptions } from "components/shared/ThreeDotMenu";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import {
    checkSimilarTermExists,
    transferWorkLabel,
    transferWorkPath,
} from "containers/TransferTermFormContainer/utils";
import { formatLocaleDateString, formatNumber, isNewDate } from "helpers/utils";
import type { Dispatch, SetStateAction } from "react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/store";
import type { ITransferTermSale } from "store/transferTermSalesStore";
import { deleteTransferTermSale, ETransferTermSalesStatus } from "store/transferTermSalesStore";
import type { ITransferTerm } from "store/transferTermsStore";

import type { IStatusRule } from "../../../components/shared/StatusChip";
import StatusChip from "../../../components/shared/StatusChip";
import DocumentsDrawer from "./DocumentsDrawer";
import styles from "./style.module.scss";
import TransferRightsModal from "./TransferRightsModal";

interface ITransferTermSalesTable {
    /**
     * All term sales.
     */
    termSales: ITransferTermSale[];
    /**
     * Count of all termSales.
     */
    termSalesCount: number;
    /**
     * Function to toggle TransferTermDrawer.
     */
    handleToggleDrawer: (
        value: boolean,
        transferTerm?: ITransferTerm | number,
        from?: "sale" | "term",
    ) => Promise<void>;
    /**
     * Status options for sales. Needed for the status column and StatusChip component.
     */
    salesStatusRules: Record<any, IStatusRule>;
    /**
     * Data and functions from useAPIPagination needed for LastElementTable.
     */
    APIData: any;
    /**
     * Type of modal to open.
     */
    modalType: string;
    setModalType: Dispatch<SetStateAction<string>>;
    /**
     * Sale being updated (for modals)
     */
    currentSale: ITransferTermSale | undefined;
    setCurrentSale: Dispatch<SetStateAction<ITransferTermSale | undefined>>;
    /**
     * Opens modal for sale actions.
     * @param event Action event.
     * @param currentSale Sale from which the action is triggered.
     * @param modalType Type of modal to open.
     * @param mode (new or edit) Needed if there are two states for the modal (mainly addRights)
     */
    handleOpeningModal: (
        currentSale: ITransferTermSale | undefined,
        modalType: string,
        mode?: string,
        event?: any,
    ) => Promise<void>;
    /**
     * Whether company has Bexio connected.
     */
    hasBexio: boolean;
    transferTerms: ITransferTerm[];
    /**
     * Handle search query
     */
    handleQuery?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    notificationPayment?: ITransferTermSale;
}

/**
 * Within TransferTerms page, first tab which displays a line per term sale (a term has as many sales as the frequence of payment)
 **/
const TransferTermSalesTable = ({
    termSales,
    termSalesCount,
    handleToggleDrawer,
    salesStatusRules,
    APIData,
    handleOpeningModal,
    setModalType,
    modalType,
    currentSale,
    setCurrentSale,
    hasBexio,
    transferTerms,
    handleQuery,
    notificationPayment,
}: ITransferTermSalesTable) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [documentsDrawerOpen, setDocumentsDrawerOpen] = useState(false);

    const { transferTermSalesStatus } = useAppSelector((state) => state.transferTermSales);

    /**
     * Options for the button on the Action column: changes dynamically depending on the status of the sale.
     * @returns Object with text and action.
     **/
    const mainActionOptions = (sale: ITransferTermSale) => {
        switch (sale.status) {
            case ETransferTermSalesStatus.DUE_NEXT:
            case ETransferTermSalesStatus.WAITING_STATEMENT:
            case ETransferTermSalesStatus.LATE_STATEMENT:
                return {
                    text: t("TransferTerms.SalesTable.EnterRights"),
                    action: (e: any, sale) => handleOpeningModal(sale, "addRights", "new", e),
                };
            case ETransferTermSalesStatus.INVOICE_TO_ISSUE:
                return {
                    text: t("TransferTerms.SalesTable.ConfirmInvoice"),
                    action: (e: any, sale) => handleOpeningModal(sale, "addInvoice", "", e),
                };
            case ETransferTermSalesStatus.WAITING_PAYMENT:
            case ETransferTermSalesStatus.LATE_PAYMENT:
                return {
                    text: t("TransferTerms.SalesTable.SetAsPaid"),
                    action: (e: any, sale) => handleOpeningModal(sale, "payStatus", "new", e),
                };
            case ETransferTermSalesStatus.PAYED:
                return {
                    text: t("TransferTerms.SalesTable.UpdatePayment"),
                    action: (e: any, sale) => handleOpeningModal(sale, "payStatus", "edit", e),
                };
            default:
                return {
                    text: "-",
                    action: () => {},
                };
        }
    };

    /**
     * Options for the three dot button.
     **/
    const actionOptions: ThreeDotMenuOptions[] = [
        {
            id: "1",
            name: t("TransferTerms.Modals.UpdateRoyalties"),
            icon: <CurrencyIcon style={{ width: "0.87rem" }} />,
            action: () => handleOpeningModal(currentSale, "addRights", "edit"),
            disabled: !!currentSale?.isDeleted,
            hide: currentSale && currentSale?.status < ETransferTermSalesStatus.INVOICE_TO_ISSUE,
        },
        {
            id: "2",
            name: t("TransferTerms.Modals.TransferRights"),
            icon: <East style={{ width: "0.87rem" }} />,
            action: () => handleOpeningModal(currentSale, "transferRights"),
            hide: currentSale?.status === ETransferTermSalesStatus.DUE_LATER,
            disabled: !!currentSale?.isDeleted || currentSale?.status !== ETransferTermSalesStatus.PAYED,
            tooltip:
                currentSale?.status !== ETransferTermSalesStatus.PAYED && !currentSale?.isDeleted
                    ? t("TransferTerms.SalesTable.DisablePayment")
                    : "",
        },
        {
            id: "3",
            name: t("TransferTerms.SalesTable.SeeContract"),
            icon: <Visibility style={{ width: "0.87rem" }} />,
            action: () => handleToggleDrawer(true, currentSale?.transferTermId, "sale"),
            disabled: !!currentSale?.isDeleted,
        },
        {
            id: "4",
            name: t("TransferTerms.SalesTable.DeletePayment"),
            icon: <DeleteOutlineOutlinedIcon sx={{ width: "15px" }} />,
            action: () => handleOpeningModal(currentSale, "deleteSale"),
            hide: currentSale?.isDeleted !== 1,
        },
    ];

    const handleToggleDocumentsDrawer = async (value: boolean, currentSale?: ITransferTermSale) => {
        currentSale && (await setCurrentSale(currentSale));
        setDocumentsDrawerOpen(value);
    };

    const handleCloseModal = (updatedSale?: ITransferTermSale) => {
        setModalType("");
        updatedSale ? setCurrentSale(updatedSale) : setCurrentSale(undefined);
    };

    const handleDeleteSale = async () => {
        if (currentSale) {
            const response = await dispatch(deleteTransferTermSale({ saleId: currentSale?.id }));
            if (response.meta.requestStatus === "fulfilled") {
                setModalType("");
            }
        }
    };

    const formatTitleColumn = (_titleName: string, termSale: ITransferTermSale) => {
        const workPath = transferWorkPath(termSale);
        return (
            <div className={styles["title-cell"]}>
                <span
                    className={styles["title-name"]}
                    onClick={() => {
                        if (workPath) navigate(workPath);
                    }}
                >
                    {transferWorkLabel(termSale)}
                </span>
                {!!termSale.isDeleted && (
                    <span className={styles["delete-info"]}>{t("TransferTerms.TermTable.TermDeleted")}</span>
                )}
            </div>
        );
    };

    const salesColumns: LastElementTableColumns[] = [
        {
            id: "transfereeName",
            label: t("TransferTerms.TermTable.Transferee"),
            align: "left",
            opacityReducer: "isDeleted",
            format: (value, id, currentRow) => {
                const termsForComparison = transferTerms.filter((item) => item.id !== currentRow.transferTermId);
                const similarTermExists = checkSimilarTermExists(
                    currentRow.countryCodes,
                    termsForComparison,
                    currentRow.workId,
                    currentRow.transfereeId,
                    "table",
                );

                return (
                    <ManagementTableAuthorComponent
                        author={currentRow?.transfereeName ?? ""}
                        avatarUrl={currentRow.transfereeAvatarUrl}
                        beneficiaryId={currentRow.transfereeId}
                        termReference={
                            similarTermExists && currentRow.termReference
                                ? currentRow.termReference
                                : similarTermExists && !currentRow.termReference
                                  ? currentRow.transferTermId
                                  : ""
                        }
                        page="transfer-terms"
                        handleQuery={handleQuery}
                    />
                );
            },
        },
        {
            id: "workName",
            label: t("TransferTerms.TermTable.Title"),
            align: "left",
            opacityReducer: "isDeleted",
            format: (value, id, currentRow) => formatTitleColumn(value, currentRow),
        },
        {
            id: "type",
            label: t("TransferTerms.SalesTable.Type"),
            align: "left",
            sort: true,
            opacityReducer: "isDeleted",
            format: (value) => (value === "advance" ? t("TransferTerms.Advance") : t("TransferTerms.Royalties")),
        },
        {
            id: "grossAmount",
            label: t("TransferTerms.TermTable.RoyaltiesCreated"),
            sort: true,
            align: "right",
            opacityReducer: "isDeleted",
            format: (value) => formatNumber(value / 100, { isCurrency: true }) ?? "-",
        },
        {
            id: "netAmount",
            label: t("TransferTerms.TermTable.RoyaltiesPaid"),
            sort: true,
            align: "right",
            opacityReducer: "isDeleted",
            format: (value, id, currentRow) => (
                <Tooltip title={currentRow?.salesId ? t("TransferTerms.SalesTable.TransferredTooltip") : ""}>
                    <div
                        className={classNames({
                            [styles["net-amount-transferred"]]: currentRow?.salesId,
                        })}
                    >
                        {currentRow?.salesId && <KeyboardDoubleArrowRightRoundedIcon />}
                        {formatNumber(value / 100, { isCurrency: true }) ?? "-"}
                    </div>
                </Tooltip>
            ),
        },
        {
            id: "dueDate",
            label: t("TransferTerms.SalesTable.DueDate"),
            sort: true,
            align: "right",
            opacityReducer: "isDeleted",
            format: (value: any) =>
                formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }),
        },
        {
            id: "paymentDate",
            label: t("TransferTerms.SalesTable.PaymentDate"),
            align: "right",
            opacityReducer: "isDeleted",
            format: (value: any) =>
                value ? formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }) : "-",
        },
        {
            id: "status",
            label: t("TransferTerms.TermTable.Status"),
            minWidth: "220px",
            sort: true,
            align: "left",
            opacityReducer: "isDeleted",
            format: (value) => <StatusChip status={value} statusRules={salesStatusRules} />,
        },
        {
            id: "mainAction",
            label: t("TransferTerms.SalesTable.Action"),
            align: "left",
            opacityReducer: "isDeleted",
            format: (value, id, currentRow) => {
                return (
                    <TextButton
                        text={mainActionOptions(currentRow)?.text ?? ""}
                        handleClick={(e) => mainActionOptions(currentRow)?.action(e, currentRow)}
                        underlineOnHover={!currentRow?.isDeleted}
                        style={{ textAlign: "left" }}
                        disabled={currentRow?.isDeleted}
                    />
                );
            },
        },
        {
            id: "actions",
            label: "",
            format: (value, id, currentRow) => (
                <ThreeDotMenu options={actionOptions} onMenuClick={() => setCurrentSale(currentRow)} />
            ),
        },
    ];

    const separatorContent = (payment: any) => (
        <TableRow id={`block-${new Date(payment.dueDate).getUTCFullYear()}`}>
            <TableCell style={{ padding: 0, border: "none" }} colSpan={10}>
                <div className="bpstatement-newperiod-separator">
                    <span>{new Date(payment.dueDate).getUTCFullYear()}</span>
                </div>
            </TableCell>
        </TableRow>
    );

    const deleteModalDescription = (
        <>
            <p>
                {t("TransferTerms.SalesTable.DeleteDescription", {
                    titleName: transferWorkLabel(currentSale ?? {}),
                    transfereeName: currentSale?.transfereeName,
                })}
            </p>
        </>
    );

    const bottomFooterContent = (
        <div style={{ paddingBlock: "16px", background: "#F6F6F6", borderTop: "1px solid #eaeaea" }}></div>
    );

    return (
        <>
            <a
                className={styles["helpdesk-link"]}
                href="https://helpdesk.crealo.app/fr/article/le-tableau-de-suivi-des-paiements-11b4hg2/"
                target="_blank"
                rel="noreferrer"
            >
                {t("TransferTerms.Helpdesk.TableDoc")}
                <TextSnippetIcon />
            </a>
            <LastElementTable
                columns={salesColumns}
                data={termSales}
                max={-1}
                dataType="transferTermSales"
                dataCount={termSalesCount}
                APIData={APIData}
                onRowClick={(id, id2, type, row: ITransferTermSale) =>
                    !row.isDeleted &&
                    row.status !== ETransferTermSalesStatus.DUE_LATER &&
                    handleToggleDocumentsDrawer(true, row)
                }
                isNewDate={(row: any, index: number, data: any) => isNewDate(row, index, data, "dueDate")}
                separatorContent={(statement: any) => separatorContent(statement)}
                allowInfiniteScroll
                maxHeight={"calc(100vh - 230px"}
                minHeight={"calc(100vh - 230px"}
                stickyFooter
                // Either scroll to year of notification paymentId or current year (which is default)
                defaultRowId={`block-${
                    new Date(notificationPayment?.dueDate ?? "").getUTCFullYear() ?? new Date().getUTCFullYear()
                }`}
                isLoading={transferTermSalesStatus === "idle"}
                bottomFooterContent={bottomFooterContent}
                columnsNumber={9}
            />
            <ModalBase
                open={modalType === "transferRights"}
                onClose={() => setModalType("")}
                modalTitle={t("TransferTerms.Modals.TransferRightsTitle")}
                modalSubtitle={t("TransferTerms.Modals.TransferRightsSubtitle")}
                infoTooltip={{
                    text: t("TransferTerms.Helpdesk.AccessDoc"),
                    link: "https://helpdesk.crealo.app/fr/article/transferer-des-droits-sur-lecran-de-gestion-1su7jcg/",
                }}
            >
                <TransferRightsModal termSale={currentSale} handleClose={handleCloseModal} />
            </ModalBase>
            <DeleteModal
                open={modalType === "deleteSale"}
                onClose={() => setModalType("")}
                elementTitle={t("TransferTerms.SalesTable.Action", {
                    duedate: currentSale && formatLocaleDateString({ date: currentSale?.dueDate }),
                })}
                onDelete={handleDeleteSale}
                description={deleteModalDescription}
            />
            <Drawer open={documentsDrawerOpen} onClose={() => setDocumentsDrawerOpen(false)} anchor="right">
                <DocumentsDrawer
                    termSale={currentSale}
                    handleOpeningModal={handleOpeningModal}
                    handleClose={() => setDocumentsDrawerOpen(false)}
                    hasBexio={hasBexio}
                />
            </Drawer>
        </>
    );
};

export default React.memo(TransferTermSalesTable);
